// QuickEdit (timesheet rules §3): one person's day in a FormDialog lg. The title is the problem as a
// sentence; under it a muted line and ‹ › through the days; then the day strip (DayStrip); the notes; and the fields: Fix, Change (a pressed part), Add
// time, Why. The footer says in one sentence exactly what Save writes, or why it's off. A locked day, a
// day with no taps, or a state not built yet shows its words and Close only. It holds no data: the view
// comes from apps/web's quick-edit model, and every press goes back out as a QuickEditAction. Reads only
// --qedit-*.
import type { ReactNode } from 'react';
import { Button } from '../../components/Button/Button.tsx';
import { Choice, ChoiceGroup } from '../../components/ChoiceGroup/ChoiceGroup.tsx';
import { DialogFrame, FormDialog } from '../../components/Dialog/Dialog.tsx';
import { TextField } from '../../components/Field/Field.tsx';
import { Layer } from '../../components/Layer/Layer.tsx';
import { Link } from '../../components/Link/Link.tsx';
import { Segmented } from '../../components/Segmented/Segmented.tsx';
import type {
  ChoicesView,
  QuickEditAction,
  QuickEditField,
  QuickEditView,
  TimeFieldView,
} from '../timesheet-view.ts';
import { DayStrip } from '../DayStrip/DayStrip.tsx';
import './QuickEdit.css';

export interface QuickEditProps {
  view: QuickEditView;
  isOpen: boolean;
  onClose: () => void;
  onAction: (a: QuickEditAction) => void;
  /** Saves; throw to keep it open with the words in red (a refusal, someone else saving first). */
  onSubmit: () => Promise<void> | void;
  /** The day strip, when the page draws its own; DayStrip from the view otherwise. */
  strip?: ReactNode;
  /** Storybook: drawn failed (the conflict's words), or asking about the change. */
  previewState?: 'failed' | 'discard';
}

export function QuickEdit({
  view,
  isOpen,
  onClose,
  onAction,
  onSubmit,
  strip,
  previewState,
}: QuickEditProps) {
  const timesheet = <Link onPress={() => onAction({ type: 'timesheet' })}>Timesheet ›</Link>;
  const top = (
    <>
      <div className="fl-qedit__head" data-span="all">
        <p className="fl-qedit__sub">{view.sub}</p>
        <Segmented
          selectionMode="none"
          label="Day"
          items={[
            { id: 'back', label: 'The day before', icon: 'chevron-left', isDisabled: !view.steps.back },
            { id: 'forward', label: 'The day after', icon: 'chevron-right', isDisabled: !view.steps.forward },
          ]}
          onAction={(id) => onAction({ type: 'step', dir: id === 'back' ? -1 : 1 })}
        />
      </div>
      <div className="fl-qedit__slot" data-span="all">
        {strip ?? <DayStrip strip={view.strip} onPart={(id) => onAction({ type: 'part', id })} />}
      </div>
      {view.notes.length ? (
        <ul className="fl-qedit__notes" data-span="all">
          {view.notes.map((n) => (
            <li key={n.text} className="fl-qedit__note" data-tone={n.tone}>
              {n.text}
            </li>
          ))}
        </ul>
      ) : null}
    </>
  );

  if (view.body.kind !== 'edit')
    return (
      <Layer level="dialog" size="lg" label={view.title} isOpen={isOpen} onClose={onClose}>
        <DialogFrame
          title={view.title}
          footer={
            <>
              <Button onPress={onClose}>Close</Button>
              <span className="fl-qedit__extra">{timesheet}</span>
            </>
          }
        >
          <div className="fl-qedit" data-body={view.body.kind}>
            {top}
            <p className="fl-qedit__words" data-span="all">
              {view.body.words}
            </p>
          </div>
        </DialogFrame>
      </Layer>
    );

  return (
    <FormDialog
      size="lg"
      title={view.title}
      isOpen={isOpen}
      onClose={onClose}
      isDirty={view.isDirty}
      onSubmit={onSubmit}
      submitLabel={view.footer.submitLabel}
      note={view.footer.note}
      isSubmitDisabled={!view.footer.canSave}
      discardText={view.discardText}
      discardLabel="Discard"
      extra={timesheet}
      previewState={previewState}
      failText={view.conflict ?? undefined}
    >
      <QuickEditFields view={view} onAction={onAction} top={top} />
    </FormDialog>
  );
}

/**
 * The edit's body: the head, the strip and the notes (`top`), then Fix, Change, Add time, the ask and
 * Why. QuickEdit draws it inside its FormDialog; a test draws it on its own.
 */
export function QuickEditFields({
  view,
  onAction,
  top,
}: {
  view: QuickEditView;
  onAction: (a: QuickEditAction) => void;
  top?: ReactNode;
}) {
  const text = (field: QuickEditField) => (t: string) => onAction({ type: 'text', field, text: t });
  return (
    <div className="fl-qedit" data-span="all">
      {top}
      {view.pending ? (
        <p className="fl-qedit__pending" data-span="all">
          {view.pending}
        </p>
      ) : null}
      {view.fix ? (
        <section className="fl-qedit__part" aria-label="Fix">
          <Choices view={view.fix} onChange={(id) => onAction({ type: 'fix', id })} />
          {view.fix.other ? <Time field={view.fix.other} onChange={text('fixTime')} /> : null}
        </section>
      ) : null}
      {view.change ? (
        <section className="fl-qedit__part" aria-labelledby="fl-qedit-change">
          <h3 className="fl-qedit__h" id="fl-qedit-change">
            {view.change.title}
          </h3>
          <div className="fl-qedit__pair">
            <Time field={view.change.start} onChange={text('start')} />
            <Time field={view.change.end} onChange={text('end')} />
          </div>
          {view.change.kind ? (
            <Choices view={view.change.kind} onChange={(code) => onAction({ type: 'kind', code })} />
          ) : null}
          <span className="fl-qedit__links">
            {view.change.addLunch ? (
              <Link onPress={() => onAction({ type: 'addLunch' })}>Add lunch inside</Link>
            ) : null}
            <Link onPress={() => onAction({ type: 'back' })}>‹ The whole day</Link>
          </span>
        </section>
      ) : null}
      {view.add ? (
        <section className="fl-qedit__part" aria-label="Add time">
          <Choices view={view.add} onChange={(code) => onAction({ type: 'add', code })} />
          {view.add.from && view.add.to ? (
            <div className="fl-qedit__pair">
              <Time field={view.add.from} onChange={text('addFrom')} />
              <Time field={view.add.to} onChange={text('addTo')} />
            </div>
          ) : null}
        </section>
      ) : null}
      {view.ask ? (
        <div className="fl-qedit__ask" data-span="all">
          <p className="fl-qedit__ask-words" role={view.ask.confirmed ? undefined : 'alert'}>
            {view.ask.text}
          </p>
          {view.ask.confirmed ? (
            <p className="fl-qedit__sub">Confirmed.</p>
          ) : (
            <>
              <Button onPress={() => onAction({ type: 'confirm' })}>{view.ask.label}</Button>
              {view.ask.decline ? (
                <Button onPress={() => onAction({ type: 'decline' })}>{view.ask.decline}</Button>
              ) : null}
            </>
          )}
        </div>
      ) : null}
      {view.why ? (
        <section className="fl-qedit__part" data-span="all" aria-label="Why">
          <Choices view={view.why} onChange={(id) => onAction({ type: 'reason', id })} />
          {view.why.other ? (
            <TextField
              label={view.why.other.label}
              placeholder={view.why.other.placeholder}
              value={view.why.other.value}
              onChange={text('whyOther')}
              error={view.why.other.error ?? null}
            />
          ) : null}
        </section>
      ) : null}
    </div>
  );
}

function Choices({ view, onChange }: { view: ChoicesView; onChange: (id: string) => void }) {
  return (
    <ChoiceGroup label={view.label} selectionMode="single" value={view.selected ?? ''} onChange={onChange}>
      {view.options.map((o) => (
        <Choice key={o.id} value={o.id} description={o.description}>
          {o.label}
        </Choice>
      ))}
    </ChoiceGroup>
  );
}

function Time({ field, onChange }: { field: TimeFieldView; onChange: (t: string) => void }) {
  if (field.fixed)
    return (
      <div className="fl-qedit__fixed">
        <span className="fl-qedit__fixed-label">{field.label}</span>
        <span className="fl-qedit__sub">{field.fixed}</span>
      </div>
    );
  return (
    <TextField
      label={field.label}
      placeholder={field.placeholder ?? 'e.g. 4:30 p.m.'}
      description={field.description}
      value={field.value}
      onChange={onChange}
      error={field.error ?? null}
    />
  );
}
